import { inject } from '@angular/core';
import { CanDeactivateFn } from '@angular/router';
import { Observable } from 'rxjs';
import { ModalService } from '../shared/modal/modal.service';

/**
 * Implemented by components that want to warn the user about unsaved changes before
 * navigating away (see `unsavedChangesGuard`).
 */
export interface CanComponentDeactivate {
  hasUnsavedChanges(): boolean;
}

/**
 * Generic `canDeactivate` guard: if the leaving component reports unsaved changes, asks the
 * user to confirm via the shared ModalService before allowing navigation away.
 */
export const unsavedChangesGuard: CanDeactivateFn<CanComponentDeactivate> = (component) => {
  if (!component.hasUnsavedChanges()) {
    return true;
  }

  const modalService = inject(ModalService);

  return new Observable<boolean>((subscriber) => {
    const dialog = modalService.showOkCancelDialog(
      'i18n.core.modal.unsavedChanges.title',
      'i18n.core.modal.unsavedChanges.question'
    );

    const okSubscription = dialog.onOkSubject.subscribe(() => {
      subscriber.next(true);
      subscriber.complete();
    });

    const cancelSubscription = dialog.onCancelSubject.subscribe(() => {
      subscriber.next(false);
      subscriber.complete();
    });

    return () => {
      okSubscription.unsubscribe();
      cancelSubscription.unsubscribe();
    };
  });
};
